import { useState } from "react";

import { useTranslation } from "react-i18next";

import useAxiosPrivate from "@/api/hooks/useAxiosPrivate";
import { ApiResources } from "@/api/resources";
import useTableItemDetails from "@/components/DetailsSection/hooks/useTableItemDetails";
import { AnalysisProviderDetailsDto } from "@/components/DetailsSection/types";
import Dialog from "@/components/Dialog/Dialog";
import { DialogWidth } from "@/components/Dialog/types/enums";
import VdoIntegrationContent from "@/components/PageHeader/actions/dialogs/integrations/VdoIntegrationContent";
import { ALERT_STATUS } from "@/context/alert/types";
import {
  VDO_INTEGRATION_FIELD_NAMES,
  VdoErrors,
  VdoObjectState
} from "@/Pages/Integrations/types";
import { useMediaQueries } from "@/shared";
import DialogActions from "@/shared/components/buttons/DialogActions";
import { refreshTableAndUpdateDetails } from "@/shared/functions/functions";
import useResponse from "@/shared/hooks/useResponse";
import { useAnalysisProvidersTableStore } from "@/stores/useTableStore";

interface EditVdoIntegrationDialogProps {
  rowData: AnalysisProviderDetailsDto;
  isOpen: boolean;
  onClose: () => void;
}

const EditVdoIntegrationDialog = ({
  rowData,
  isOpen,
  onClose
}: EditVdoIntegrationDialogProps) => {
  const { toMd } = useMediaQueries();
  const { t } = useTranslation();
  const { putData } = useAxiosPrivate();
  const { getTableItemDetails } = useTableItemDetails();
  const { handleResponse, handleCommonError } = useResponse();

  const params = useAnalysisProvidersTableStore(state => state.params);
  const { contains: searchParams, selectedTableItem } = params;

  const { company, username, name, enabled, fileFormat, includeSubCompanies } =
    rowData;

  const fileFormatObj = {
    name: t(`Settings##file formats##${fileFormat}`),
    value: fileFormat
  };

  const initialIntegrationObject: VdoObjectState = {
    [VDO_INTEGRATION_FIELD_NAMES.SelectedCompany]: company || null,
    [VDO_INTEGRATION_FIELD_NAMES.Name]: name,
    [VDO_INTEGRATION_FIELD_NAMES.ApiKey]: "",
    [VDO_INTEGRATION_FIELD_NAMES.FileFormat]: fileFormatObj,
    [VDO_INTEGRATION_FIELD_NAMES.AccountId]: username,
    [VDO_INTEGRATION_FIELD_NAMES.Enabled]: enabled,
    [VDO_INTEGRATION_FIELD_NAMES.IncludeSubCompanies]: includeSubCompanies
  };

  const [vdoObject, setVdoObject] = useState<VdoObjectState>(
    initialIntegrationObject
  );

  const initialErrors: VdoErrors = {
    [VDO_INTEGRATION_FIELD_NAMES.ApiKey]: "",
    [VDO_INTEGRATION_FIELD_NAMES.AccountId]: "",
    [VDO_INTEGRATION_FIELD_NAMES.Name]: ""
  };

  const [errors, setErrors] = useState<VdoErrors>(initialErrors);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [hasInteracted, setHasInteracted] = useState<boolean>(false);

  const hasErrors = Object.values(errors).some(error => error !== "");

  const optionalField = VDO_INTEGRATION_FIELD_NAMES.ApiKey;

  const requiredFieldsFilled = Object.entries(vdoObject).every(
    ([key, value]) => {
      if (key === optionalField) return true;
      return (
        value !== null && value !== undefined && String(value).trim() !== ""
      );
    }
  );

  const successMessage = t("Alert##edit integration success");

  const resetAllValues = () => {
    setVdoObject(initialIntegrationObject);
    setErrors(initialErrors);
    setHasInteracted(false);
  };

  const handleEditSubmit = async () => {
    setIsLoading(true);
    const editableItemHasOpenedDetails = selectedTableItem === rowData.id;

    try {
      const { selectedCompany, ...cleanedvdoObject } = vdoObject;

      const payloadObject = {
        ...cleanedvdoObject,
        id: rowData.id,
        companyId: vdoObject.selectedCompany?.id,
        fileFormat: vdoObject.fileFormat?.value
      };

      const { data } = await putData(
        ApiResources.IntegrationsAnalysisProviders,
        payloadObject
      );

      refreshTableAndUpdateDetails({
        valueInSearchField: searchParams,
        resourceId: data.id,
        successMessage,
        itemHasOpenedDetails: editableItemHasOpenedDetails,
        getTableItemDetails,
        handleResponse
      });

      onClose();
      resetAllValues();
      handleResponse(ALERT_STATUS.Success, successMessage);
    } catch (error) {
      handleCommonError(error, t);
    }

    setIsLoading(false);
  };

  const handleClose = () => {
    !isLoading && onClose();
  };

  const isReadyToConfirm = !hasErrors && requiredFieldsFilled && hasInteracted;

  return (
    <Dialog
      data-testid="edit-vdo-integration-dialog"
      title={t("Dialog##vdo integration##edit##title")}
      description={t("Dialog##vdo integration##edit##description")}
      isTitleSeparator
      isActionsSeparator
      extendToMaxWidth={toMd}
      open={isOpen}
      close={handleClose}
      submit={handleEditSubmit}
      width={toMd ? undefined : DialogWidth.Dialog920px}
      customPaperMargin="48px"
      actions={
        <DialogActions
          onClose={handleClose}
          isLoading={isLoading}
          disabledClose={isLoading}
          isreadyToConfirm={isReadyToConfirm}
          confirmationButtonText={t("Button##save")}
          confirmationButtonTestId="edit-save-vdo-integration-button"
          cancelButtonText={t("Button##close")}
          cancelButtonTestId="edit-close-vdo-integration-button"
        />
      }
    >
      <VdoIntegrationContent
        optionalApiKey
        hasInteracted={hasInteracted}
        setHasInteracted={setHasInteracted}
        isLoading={isLoading}
        vdoObject={vdoObject}
        errors={errors}
        setErrors={setErrors}
        setVdoObject={setVdoObject}
      />
    </Dialog>
  );
};

export default EditVdoIntegrationDialog;
